// Cookie / storage consent.
//
// --- what WallBurst actually stores today ---------------------------------
//
// This was audited before the banner was written, because a consent notice
// that does not match reality is worse than none: it trains people to dismiss
// it, and it misdescribes the site in a document they are being asked to rely
// on.
//
//   * NO cookies are set by this app. `document.cookie` appears nowhere.
//   * Auth uses localStorage: token, role, roles, username, vendor_id,
//     customer_id. That is strictly necessary -- it is what keeps you signed
//     in -- and under GDPR/ePrivacy strictly necessary storage does not
//     require consent. It is listed here so the page can say so plainly.
//   * Stripe.js sets its own fraud-prevention cookies, but it is loaded ONLY
//     on the customer payment routes, not site-wide, and only for a signed-in
//     customer who is paying. That is the configuration that keeps it
//     defensible as strictly necessary.
//   * There is no analytics, no tag manager, no advertising pixel. None.
//
// So the `analytics` category below currently gates nothing. It exists so that
// the day something is added, the gate is already in place and already
// respected -- the alternative is adding the tool first and the consent check
// later, which is how tracking ships un-gated.
//
// --- the thing a banner does NOT fix --------------------------------------
//
// public/index.html loads fonts from fonts.googleapis.com / fonts.gstatic.com.
// That sets no cookie, but it does send every visitor's IP address to Google
// on every page load, before any banner is answered. Consent UI cannot fix
// that; self-hosting the two font files can, and that is the higher-value
// change of the two.

const KEY = "wb_consent";

// Bump when the categories change meaning. A stored decision against an older
// version is treated as absent and asked again, rather than being silently
// reinterpreted as agreement to something new.
export const CONSENT_VERSION = 1;

export const CATEGORIES = {
    necessary: {
        label: "Strictly necessary",
        required: true,
        description:
            "Keeps you signed in and lets checkout work. Stored in your browser, " +
            "never used to track you, and the site cannot function without it.",
    },
    analytics: {
        label: "Analytics",
        required: false,
        description:
            "Would let us count page visits to see what is useful. We do not " +
            "currently use any analytics at all — this switch is here so that if " +
            "we ever add it, your answer already applies.",
    },
};

const DENY_ALL = { necessary: true, analytics: false };
const ALLOW_ALL = { necessary: true, analytics: true };

const listeners = new Set();

function read() {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (!saved || saved.version !== CONSENT_VERSION) return null;
        return saved;
    } catch {
        // Private mode, disabled storage, or corrupt JSON. Treated as "not
        // asked yet" rather than as consent.
        return null;
    }
}

/** The stored decision, or null if the visitor has not answered. */
export function getConsent() {
    const saved = read();
    return saved ? saved.choices : null;
}

/** Has this visitor answered the banner? */
export function hasDecided() {
    return read() !== null;
}

/**
 * Is this category permitted?
 *
 * Fails CLOSED: no decision means no consent. Anything gated behind this stays
 * off until somebody actively says yes, which is the whole point -- an opt-out
 * default is not consent under GDPR.
 */
export function allows(category) {
    if (CATEGORIES[category] && CATEGORIES[category].required) return true;
    const choices = getConsent();
    return !!(choices && choices[category]);
}

export function saveConsent(choices) {
    const merged = { ...DENY_ALL, ...choices, necessary: true };
    try {
        window.localStorage.setItem(
            KEY,
            JSON.stringify({
                version: CONSENT_VERSION,
                // Recorded so that "when did they agree, and to what" is
                // answerable. Consent you cannot evidence is not much use.
                decidedAt: new Date().toISOString(),
                choices: merged,
            })
        );
    } catch {
        // Nothing to do. The visitor is asked again next time, which is the
        // safe direction to fail in.
    }
    listeners.forEach((fn) => fn(merged));
    return merged;
}

export const acceptAll = () => saveConsent(ALLOW_ALL);
export const rejectAll = () => saveConsent(DENY_ALL);

/** Re-open the banner, for the footer's "Cookie settings" link. */
export function resetConsent() {
    try {
        window.localStorage.removeItem(KEY);
    } catch {
        /* ignore */
    }
    listeners.forEach((fn) => fn(null));
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function onConsentChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}
